import { ApiError, jsonFromResponse, normalizeApiError } from "@/shared/api/client";
import { trackPublicWebEvent } from "@/shared/analytics/analytics";
import { authFetch, getWebDeviceId } from "@/shared/auth/session";
import { defaultBriefSequenceForCountry } from "../brief/default-sequence";
import { publicWebCopyForLang } from "@/shared/i18n/public-web-copy";
import { dashboardDeviceSpeech } from "./dashboard-device-speech";
import {
  DAILY_DEFAULT_AUDIO_DEVICE_SPEECH_REQUIRED,
  buildDailyDefaultEditionUrl,
  canonicalDailyDefaultSpeechBody,
  dailyDefaultDeviceSpeechResponseMatches,
  dailyDefaultEditionPlaylistBlob,
  dailyDefaultOfferForBriefRunError,
  dailyDefaultOfferForDashboardError,
  isDailyDefaultSystemBudgetError,
  normalizeDailyDefaultCountry,
  normalizeDailyDefaultEdition,
  normalizeDailyDefaultLanguage,
  type DailyDefaultAudioEdition,
  type DailyDefaultAudioOfferIdentity,
} from "./daily-default-edition-audio-contract.mjs";
import { startDailyDefaultEditionPlaylist } from "./daily-default-edition-playlist.mjs";
import {
  hasCompatibleDeviceSpeechVoice,
  resolveDeviceSpeechLocale,
  waitForCompatibleDeviceSpeechVoice,
} from "./device-speech-fallback.mjs";
import type { AudioPlaybackState, DailyDefaultAudioOffer } from "./audio-playback-state";

type RequestOwnership = {
  signal: AbortSignal;
  finish: () => void;
  isCurrent: () => boolean;
};

type DeviceSpeechHooks = Parameters<typeof dashboardDeviceSpeech.prepare>[0]["hooks"];

type DailyDefaultAudioRuntime = {
  beginRequest: () => RequestOwnership;
  createPlayer: () => HTMLAudioElement;
  clearPlayer: (
    player: HTMLAudioElement,
    requestObjectUrl: string | null,
    preserveReusedActivePlayer?: boolean,
    preserveMediaSession?: boolean,
  ) => void;
  deviceSpeechHooks: (
    key: string,
    label: string,
    detail: string | undefined,
    onAttemptSettled?: () => void,
  ) => DeviceSpeechHooks;
  getRate: () => number;
  getState: () => AudioPlaybackState;
  isActivePlayer: (player: HTMLAudioElement) => boolean;
  onEnded: (key: string) => void;
  onMp3Started: (state: AudioPlaybackState) => void;
  replaceObjectUrl: (value: string | null) => void;
  setMediaSession: (label: string, detail: string | undefined, lang: string) => void;
  setState: (state: AudioPlaybackState) => void;
  setTimelineState: (state: AudioPlaybackState) => void;
  stop: () => void;
  updateMediaSessionPosition: () => void;
};

type DailyDefaultAudioContext = DailyDefaultAudioOfferIdentity & {
  requestBody: Record<string, unknown>;
  key: string;
  sourceLabel: string;
  sourceDetail?: string;
};

let runtime: DailyDefaultAudioRuntime | null = null;
let offerContext: DailyDefaultAudioContext | null = null;

function finiteDuration(player: HTMLAudioElement) {
  return Number.isFinite(player.duration) && player.duration > 0 ? player.duration : 0;
}

function bufferedFraction(player: HTMLAudioElement, duration = finiteDuration(player)) {
  if (duration <= 0 || player.buffered.length === 0) return 0;
  try {
    return Math.min(1, Math.max(0, player.buffered.end(player.buffered.length - 1) / duration));
  } catch {
    return 0;
  }
}

function publicOffer(
  identity: DailyDefaultAudioOfferIdentity,
  status: DailyDefaultAudioOffer["status"] = "ready",
): DailyDefaultAudioOffer {
  return {
    countryCode: identity.countryCode,
    languageCode: identity.languageCode,
    tier: identity.tier,
    showSubscribeCta: identity.showSubscribeCta,
    status,
  };
}

export function installDailyDefaultEditionAudioRuntime(value: DailyDefaultAudioRuntime) {
  runtime = value;
}

export function captureDailyDefaultEditionAudioOffer(
  path: string,
  rawError: unknown,
  requestBody: Record<string, unknown>,
  key: string,
  label: string,
  detail?: string,
): DailyDefaultAudioOffer | undefined {
  const error = normalizeApiError(rawError, "audio_unavailable");
  const identity = dailyDefaultOfferForDashboardError({
    path,
    error,
    requestBody,
  });
  if (!identity) return undefined;
  offerContext = {
    ...identity,
    requestBody,
    key,
    sourceLabel: label,
    sourceDetail: detail,
  };
  return publicOffer(identity);
}

/** Present one owner-fenced offer when narrated Brief preparation hits the exact user budget. */
export function captureBriefRunDailyDefaultEditionAudioOffer(
  rawError: unknown,
  requestBody: Record<string, unknown>,
): boolean {
  if (!runtime) return false;
  const error = normalizeApiError(rawError, "audio_unavailable");
  const identity = dailyDefaultOfferForBriefRunError({ error, requestBody });
  if (!identity) return false;
  const copy = publicWebCopyForLang(identity.languageCode);
  const context: DailyDefaultAudioContext = {
    ...identity,
    requestBody,
    key: `brief-section:daily-default:${identity.countryCode}:${identity.languageCode}`,
    sourceLabel: copy("audio.dailyDefault.label"),
    sourceDetail: copy("audio.dailyDefault.detail"),
  };
  offerContext = context;
  runtime.setState({
    phase: "error",
    key: context.key,
    label: context.sourceLabel,
    detail: copy("audio.error.premiumDailyBudget"),
    code: "PAID_API_USER_DAILY_BUDGET_EXCEEDED",
    httpStatus: error.status,
    quotaKind: "paid_user_budget",
    showSubscribeCta: identity.showSubscribeCta,
    dailyDefaultAudioOffer: publicOffer(identity),
  });
  return true;
}

/** Stop a narrated Brief run on the canonical system budget without offering fallback audio. */
export function captureBriefRunSystemBudgetError(
  rawError: unknown,
  requestBody: Record<string, unknown>,
): boolean {
  if (!runtime) return false;
  const error = normalizeApiError(rawError, "audio_unavailable");
  if (!isDailyDefaultSystemBudgetError(error)) return false;
  const countryCode = normalizeDailyDefaultCountry(requestBody.country);
  const languageCode = normalizeDailyDefaultLanguage(requestBody.lang);
  if (!countryCode || !languageCode) return false;
  const copy = publicWebCopyForLang(languageCode);
  offerContext = null;
  runtime.setState({
    phase: "error",
    key: `brief-section:system-budget:${countryCode}:${languageCode}`,
    label: copy("audio.dailyDefault.label"),
    detail: copy("audio.error.systemDailyBudget"),
    code: "PAID_API_SYSTEM_DAILY_BUDGET_EXCEEDED",
    httpStatus: error.status,
    quotaKind: "system_budget",
    showSubscribeCta: false,
  });
  return true;
}

function offerFailure(context: DailyDefaultAudioContext): AudioPlaybackState {
  const next: AudioPlaybackState = {
    phase: "error",
    key: context.key,
    label: context.sourceLabel,
    detail: publicWebCopyForLang(context.languageCode)("audio.dailyDefault.unavailable"),
    code: "daily_default_audio_unavailable",
    quotaKind: "paid_user_budget",
    dailyDefaultAudioOffer: publicOffer(context, "error"),
  };
  runtime?.setState(next);
  return next;
}

function prepareSelectedLanguageDeviceSpeech(
  context: DailyDefaultAudioContext,
  player: HTMLAudioElement,
): AudioPlaybackState | null {
  if (!runtime) return null;
  let released = false;
  const releasePrimer = () => {
    if (released) return;
    released = true;
    runtime?.clearPlayer(player, null, false, true);
  };
  const copy = publicWebCopyForLang(context.languageCode);
  const canonicalBody = canonicalDailyDefaultSpeechBody({
    requestBody: context.requestBody,
    defaultSectionIds: defaultBriefSequenceForCountry(context.countryCode),
    countryCode: context.countryCode,
    languageCode: context.languageCode,
  });
  if (!canonicalBody) {
    releasePrimer();
    return null;
  }
  const prepared = dashboardDeviceSpeech.prepare({
    body: canonicalBody,
    kind: "dashboard",
    key: context.key,
    label: copy("audio.dailyDefault.label"),
    detail: copy("audio.dailyDefault.detail"),
    lang: context.languageCode,
    rate: runtime.getRate(),
    hooks: runtime.deviceSpeechHooks(
      context.key,
      copy("audio.dailyDefault.label"),
      copy("audio.dailyDefault.detail"),
      releasePrimer,
    ),
  });
  if (!prepared) {
    releasePrimer();
    return null;
  }
  return dashboardDeviceSpeech.startDashboardPreparedAfterAuthorization();
}

function acceptedDeviceSpeechHandoff(
  state: AudioPlaybackState | null,
  context: DailyDefaultAudioContext,
): state is AudioPlaybackState {
  if (!state || state.key !== context.key) return false;
  if (state.phase === "loading" || state.phase === "playing") return true;
  return state.phase === "error" && state.deviceSpeechReady === true;
}

function installPlaylistPlayback(args: {
  context: DailyDefaultAudioContext;
  edition: DailyDefaultAudioEdition;
  ownership: RequestOwnership;
  player: HTMLAudioElement;
}) {
  const { context, edition, ownership, player } = args;
  const installedRuntime = runtime;
  if (!installedRuntime) return Promise.resolve();
  const active = () => installedRuntime.isActivePlayer(player) && ownership.isCurrent();
  const copy = publicWebCopyForLang(context.languageCode);
  const label = copy("audio.dailyDefault.label");
  const detail = copy("audio.dailyDefault.detail");
  const timeline = (overrides: Pick<AudioPlaybackState, "buffering" | "paused"> = {}) => {
    if (!active() || installedRuntime.getState().phase !== "playing") return;
    const duration = finiteDuration(player);
    installedRuntime.setTimelineState({
      ...installedRuntime.getState(),
      positionMs: Math.round(Math.max(0, player.currentTime || 0) * 1000),
      durationMs: Math.round(duration * 1000),
      buffered01: bufferedFraction(player, duration),
      playbackRate: player.playbackRate || installedRuntime.getRate(),
      ...overrides,
    });
    installedRuntime.updateMediaSessionPosition();
  };
  return startDailyDefaultEditionPlaylist({
    player,
    items: edition.items,
    isActive: active,
    playbackRate: installedRuntime.getRate,
    createObjectUrl: (items) => URL.createObjectURL(dailyDefaultEditionPlaylistBlob(items)),
    replaceObjectUrl: installedRuntime.replaceObjectUrl,
    clearPlayer: (requestObjectUrl) => installedRuntime.clearPlayer(player, requestObjectUrl),
    onLoading: () => {
      installedRuntime.setState({
        phase: "loading",
        key: context.key,
        label,
        detail,
        playbackRate: installedRuntime.getRate(),
        cache: "daily-default-edition",
        canDownload: false,
      });
    },
    onPlaying: () => {
      installedRuntime.setMediaSession(label, detail, context.languageCode);
      const state: AudioPlaybackState = {
        phase: "playing",
        key: context.key,
        label,
        detail,
        paused: false,
        buffering: false,
        positionMs: Math.round(Math.max(0, player.currentTime || 0) * 1000),
        durationMs: Math.round(finiteDuration(player) * 1000),
        buffered01: bufferedFraction(player),
        playbackRate: player.playbackRate || installedRuntime.getRate(),
        cache: "daily-default-edition",
        canDownload: false,
      };
      installedRuntime.setState(state);
      installedRuntime.onMp3Started(state);
    },
    onTimeline: timeline,
    onFailed: () => {
      ownership.finish();
      if (offerContext === context) offerFailure(context);
    },
    onFinished: () => {
      ownership.finish();
      if (offerContext !== context) return;
      offerContext = null;
      installedRuntime.setState({ phase: "idle" });
      installedRuntime.onEnded(context.key);
    },
  });
}

export async function continueWithDailyDefaultEditionAudio(): Promise<AudioPlaybackState | null> {
  const context = offerContext;
  if (!context || !runtime) return null;
  const ownership = runtime.beginRequest();
  ownership.signal.addEventListener(
    "abort",
    () => {
      if (offerContext === context) offerContext = null;
    },
    { once: true },
  );
  // This must happen before the first await so Safari/iOS retains the user's
  // activation while the selected market/language edition is fetched.
  const player = runtime.createPlayer();
  dashboardDeviceSpeech.discard();
  const selectedLocale = resolveDeviceSpeechLocale(
    context.languageCode,
    context.countryCode,
  );
  const voiceProbeController = new AbortController();
  ownership.signal.addEventListener(
    "abort",
    () => voiceProbeController.abort(),
    { once: true },
  );
  const compatibleVoice = selectedLocale
    ? waitForCompatibleDeviceSpeechVoice(selectedLocale, {
        signal: voiceProbeController.signal,
      })
    : Promise.resolve(false);
  runtime.setState({
    ...runtime.getState(),
    phase: "error",
    dailyDefaultAudioOffer: publicOffer(context, "loading"),
  });
  try {
    const response = await authFetch(
      buildDailyDefaultEditionUrl({ ...context, clientDeviceId: getWebDeviceId() }),
      {
        method: "GET",
        activitySource: "user_initiated",
        signal: ownership.signal,
      },
    );
    let rawEdition: unknown;
    try {
      rawEdition = await jsonFromResponse<unknown>(response, {
        fallbackCode: "daily_default_audio_unavailable",
      });
    } catch (rawError) {
      const error = normalizeApiError(rawError, "daily_default_audio_unavailable");
      if (!ownership.isCurrent() || offerContext !== context) {
        voiceProbeController.abort();
        runtime.clearPlayer(player, null, true);
        return runtime.getState();
      }
      const selectedLanguageDeviceSpeechRequired =
        error.status === 409 &&
        error.code === DAILY_DEFAULT_AUDIO_DEVICE_SPEECH_REQUIRED &&
        dailyDefaultDeviceSpeechResponseMatches(error.body, context);
      if (selectedLanguageDeviceSpeechRequired) {
        const voiceAvailable =
          (await compatibleVoice) ||
          Boolean(selectedLocale && hasCompatibleDeviceSpeechVoice(selectedLocale));
        if (!ownership.isCurrent() || offerContext !== context) {
          voiceProbeController.abort();
          runtime.clearPlayer(player, null, true);
          return runtime.getState();
        }
        const next = voiceAvailable
          ? prepareSelectedLanguageDeviceSpeech(context, player)
          : null;
        if (!acceptedDeviceSpeechHandoff(next, context)) {
          throw new ApiError({ status: 409, code: "daily_default_audio_unavailable" });
        }
        voiceProbeController.abort();
        offerContext = null;
        ownership.finish();
        return next;
      }
      throw error;
    }
    voiceProbeController.abort();
    if (!ownership.isCurrent() || offerContext !== context) {
      runtime.clearPlayer(player, null, true);
      return runtime.getState();
    }
    const edition = normalizeDailyDefaultEdition(rawEdition, {
      ...context,
      defaultSectionIds: defaultBriefSequenceForCountry(context.countryCode),
    });
    await installPlaylistPlayback({ context, edition, ownership, player });
    return runtime.getState();
  } catch {
    voiceProbeController.abort();
    const current = ownership.isCurrent() && offerContext === context;
    runtime.clearPlayer(player, null, !current);
    if (!current) return runtime.getState();
    dashboardDeviceSpeech.discard();
    ownership.finish();
    return offerFailure(context);
  }
}

export function dismissDailyDefaultEditionAudio() {
  offerContext = null;
  runtime?.stop();
}

export function clearDailyDefaultEditionAudioContext() {
  offerContext = null;
}

export function trackDailyDefaultEditionSubscribe() {
  trackPublicWebEvent("public_web_premium_cta_tapped", {
    source: "dashboard_audio_user_budget",
  });
}
